// theme/config.ts
import { defineConfig } from '@chakra-ui/react';

export const colors = {
	primary: '#1C4B74',
	errors: '#EB0000',
	warning: '#F8D356',
	warning300: '#E1BA1C',
	warning50: '#FFE7B9',
	success50: '#B9FFC4',
	transparent: 'transparent',
	success: '#117964',
	oceanBlue: '#1C4D74',
	darkOpacity: 'rgba(0, 0, 0, 0.5)',
	robotGrey: '#7C7C7C',
	error300: '#EA6949',
	white: '#FFFFFF',
	black: '#000000',
	menuListShadow: '#00000040',
	aiBoxShadow: '#11796440',
	textBackShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
	lightGray: '#D9D9D9',
	blueGray: '#DEE2E9',
	muted: '#ABB3C7',
	offWhite: '#FBFBFB',
	midnightBlack: '#181924',
	darkModeBgColor: '#292929',
	jetBlack: '#18181B',
	ironGray: '#2A2A2A',
	charcoalGray: '#27272A',
	// Drag item background colors
	softGray: '#f2f2f2',
	darkCharcoal: '#343434',
	// Chart colors
	imbedGray: '#C7C7C7',
	nvisionPurple: '#A29FE4',
	axolotlBlue: '#556DB7',
	creamBeige: '#F1EDE9',
	traidPink: '#AE8BB5',
	// Gradient colors for donut charts (blue to teal gradient)
	chartGradient1: '#1C4B74',
	chartGradient2: '#19586F',
	chartGradient3: '#16656B',
	chartGradient4: '#156B69',
	chartGradient5: '#117964',
	chartGradient6: '#029477',
	// Status colors
	deniedRed: '#C0392B',
	pendingYellow: '#E1BA1C',
	statusTeal: '#26A69A',
	lightBlue: '#3498DB',
	statusOrange: '#F39C12',
	errorDarkRed: '#922B21',
	defaultGray: '#7F8C8D',
	loader1: '#1C4B74',
	loader2: '#1A5D7F',
	loader3: '#117964',
	loader4: '#0D5E4F',
	loader5: '#0A4A3E',
	// Toast colors
	toastSuccess: {
		bg: '#F0FDF4',
		text: '#166534',
		border: '#166534',
		icon: '#16A34A',
	},
	toastError: {
		bg: '#FEF2F2',
		text: '#991B1B',
		border: '#991B1B',
		icon: '#DC2626',
	},
	tabelCellDark: '#252525',
	tabelHeaderCellDark: '#2A2A2A',
	surfaceGrey: '#292929',
	hoverBg: '#FFFFFF14',
	deepAbyss: '#181924',
	// BV Status colors
	bvApproved: '#117964',
	bvAuthApproved: '#26A69A',
	bvAppealInProcess: '#F39C12',
	bvInProcess: '#1C4B74',
	bvPendingClinicResponse: '#E1BA1C',
	bvVoid: '#7F8C8D',
	bvDenied: '#C0392B',
	bvAuthDenied: '#922B21',
	bvOrderPlaced: '#34495E',
	bvMixed: '#EB9900',
	formBgDark: '#161616ff',
};

export const config = defineConfig({
	conditions: {
		unchecked: '&[data-state=unchecked]',
	},
	theme: {
		tokens: {
			fonts: {
				body: { value: 'Lato, sans-serif' },
				heading: { value: 'Lato, sans-serif' },
			},
			colors: {
				brand: {
					50: { value: '#e6f2ff' },
					100: { value: '#e6f2ff' },
					200: { value: '#bfdeff' },
					300: { value: '#99caff' },
					500: { value: '#1C4B74' },
					600: { value: '#117964' },
					950: { value: '#001a33' },
				},
				loader1: { value: colors.loader1 },
				loader2: { value: colors.loader2 },
				loader3: { value: colors.loader3 },
				loader4: { value: colors.loader4 },
				loader5: { value: colors.loader5 },
				input: {
					bg: { value: colors.white },
					text: { value: '#000000' },
					placeholder: { value: colors.lightGray },
				},
				modalMessage: {
					bg: { value: colors.white },
					bgDark: { value: '#171616' },
				},
				overlay: {
					dark: { value: colors.darkOpacity },
				},
				errors: { value: colors.errors },
				error300: { value: colors.error300 },
				cardBackground: {
					bg: { value: colors.white },
					bgDark: { value: colors.charcoalGray },
				},
				draggableItemBackground: {
					bg: { value: colors.softGray },
					bgDark: { value: colors.darkCharcoal },
				},
				pageBackground: {
					bg: { value: colors.white },
					bgDark: { value: colors.jetBlack },
				},
				loaderBackground: {
					bg: { value: colors.white },
					bgDark: { value: colors.deepAbyss },
				},
				headerBackground: {
					bg: { value: colors.offWhite },
					bgDark: { value: colors.ironGray },
				},
				warning: { value: colors.warning },
				warning300: { value: colors.warning300 },
				warning50: { value: colors.warning50 },
				success50: { value: colors.success50 },
				text: {
					dark: { value: '#000000' },
					light: { value: '#FFFFFF' },
					muted: { value: colors.muted },
				},
				textBackShadow: { value: colors.textBackShadow },
				lightGray: { value: colors.lightGray },
				robotGrey: { value: colors.robotGrey },
				blueGray: { value: colors.blueGray },
				surfaceGrey: { value: colors.surfaceGrey },
				white: { value: colors.white },
				offWhite: { value: colors.offWhite },
				success: { value: colors.success },
				oceanBlue: { value: colors.oceanBlue },
				midnightBlack: { value: colors.midnightBlack },
				ironGray: { value: colors.ironGray },
				jetBlack: { value: colors.jetBlack },
				darkModeBgColor: { value: colors.darkModeBgColor },
				// Chart colors
				imbedGray: { value: colors.imbedGray },
				nvisionPurple: { value: colors.nvisionPurple },
				axolotlBlue: { value: colors.axolotlBlue },
				creamBeige: { value: colors.creamBeige },
				traidPink: { value: colors.traidPink },
				// Status colors
				deniedRed: { value: colors.deniedRed },
				pendingYellow: { value: colors.pendingYellow },
				statusTeal: { value: colors.statusTeal },
				lightBlue: { value: colors.lightBlue },
				statusOrange: { value: colors.statusOrange },
				errorDarkRed: { value: colors.errorDarkRed },
				defaultGray: { value: colors.defaultGray },
				// Toast colors
				toastSuccess: {
					bg: { value: colors.toastSuccess.bg },
					text: { value: colors.toastSuccess.text },
					border: { value: colors.toastSuccess.border },
					icon: { value: colors.toastSuccess.icon },
				},
				toastError: {
					bg: { value: colors.toastError.bg },
					text: { value: colors.toastError.text },
					border: { value: colors.toastError.border },
					icon: { value: colors.toastError.icon },
				},
			},
		},
		semanticTokens: {
			colors: {
				messageBg: {
					value: {
						base: '{colors.modalMessage.bg}',
						_dark: '{colors.modalMessage.bgDark}',
					},
				},
				overlay: {
					dark: { value: '{colors.overlay.dark}' },
				},
				brand: {
					text: {
						value: {
							base: '{colors.text.light}',
							_dark: '{colors.text.dark}',
						},
					},
					textDark: {
						value: {
							base: '{colors.text.dark}',
							_dark: '{colors.text.light}',
						},
					},
					textWhite: {
						value: '{colors.text.light}',
					},
					textBackShadow: { value: '{colors.textBackShadow}' },
					errors: { value: '{colors.errors}' },
					error300: { value: '{colors.error300}' },
					solid: { value: '{colors.brand.500}' },
					solidSecondary: { value: '{colors.brand.600}' },
					solidGradient: {
						value: {
							base: 'linear-gradient(90deg, {colors.brand.500} 0%, {colors.brand.600} 100%)',
						},
					},
					inputBg: {
						value: {
							base: '{colors.gray.50}',
							_dark: '{colors.transparent}',
						},
					},
					inputText: {
						value: {
							base: '{colors.input.text}',
							_dark: '{colors.brand.100}',
						},
					},
					inputPlaceholder: {
						value: {
							base: '{colors.input.placeholder}',
							_dark: '{colors.input.placeholder}',
						},
					},
					messageBg: {
						value: {
							base: '{colors.modalMessage.bg}',
							_dark: '{colors.modalMessage.bgDark}',
						},
					},
					contrast: { value: '{colors.brand.100}' },
					fg: { value: '{colors.brand.700}' },
					muted: { value: '{colors.brand.100}' },
					subtle: { value: '{colors.brand.200}' },
					emphasized: { value: '{colors.brand.300}' },
					focusRing: { value: '{colors.brand.500}' },
					cardBackground: {
						value: {
							base: '{colors.cardBackground.bg}',
							_dark: '{colors.cardBackground.bgDark}',
						},
					},
					draggableItemBackground: {
						value: {
							base: '{colors.draggableItemBackground.bg}',
							_dark: '{colors.draggableItemBackground.bgDark}',
						},
					},
					pageBackground: {
						value: {
							base: '{colors.pageBackground.bg}',
							_dark: '{colors.pageBackground.bgDark}',
						},
					},
					loaderBackground: {
						value: {
							base: '{colors.loaderBackground.bg}',
							_dark: '{colors.loaderBackground.bgDark}',
						},
					},
					headerBackground: {
						value: {
							base: '{colors.headerBackground.bg}',
							_dark: '{colors.headerBackground.bgDark}',
						},
					},
					warning: { value: '{colors.warning}' },
					warning300: { value: '{colors.warning300}' },
					warning50: { value: '{colors.warning50}' },
					success50: { value: '{colors.success50}' },
					lightGray: { value: '{colors.lightGray}' },
					robotGrey: { value: '{colors.robotGrey}' },
					white: { value: '{colors.white}' },
					success: { value: '{colors.success}' },
					toast: {
						success: {
							bg: { value: '{colors.toastSuccess.bg}' },
							text: { value: '{colors.toastSuccess.text}' },
							border: { value: '{colors.toastSuccess.border}' },
							icon: { value: '{colors.toastSuccess.icon}' },
						},
						error: {
							bg: { value: '{colors.toastError.bg}' },
							text: { value: '{colors.toastError.text}' },
							border: { value: '{colors.toastError.border}' },
							icon: { value: '{colors.toastError.icon}' },
						},
					},
				},
				contrastBorder: {
					value: {
						base: '#000000',
						_dark: '#FFFFFF',
					},
				},
			},
		},
	},
});
